/**
 * Authentication — layer 1 (`hd` hint) of the three-layer access model, plus
 * the client-side session lifecycle (doc 147 §8.2).
 *
 * `mapToInternalUser` is the pure, unit-tested validation core; the SDK calls
 * around it are thin. Domain enforcement is server-side (`beforeUserSignedIn`);
 * the `role` claim is the authorization token the server minted at sign-in.
 */
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { auth } from './client.js';
import type { InternalUser } from '../state/store.js';

const VALID_ROLES = ['engineer', 'designer', 'admin'] as const;

/**
 * Map a Firebase user + its custom claims to an {@link InternalUser}, or `null`
 * when the account fails the internal-user contract (unverified email or a
 * missing/invalid role claim). Domain validation is performed server-side by
 * `beforeUserSignedIn` — the role claim is proof that it passed.
 */
export function mapToInternalUser(
  user: Pick<User, 'uid' | 'email' | 'displayName' | 'emailVerified'> | null,
  claims: Record<string, unknown> | null | undefined,
): InternalUser | null {
  if (!user || !user.email || !user.emailVerified) return null;
  const role = claims?.role;
  if (typeof role !== 'string' || !(VALID_ROLES as readonly string[]).includes(role)) {
    return null;
  }
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName ?? user.email.split('@')[0] ?? user.email,
    role: role as InternalUser['role'],
  };
}

/* c8 ignore start — thin Firebase Auth SDK wrappers; emulator/manual tested. */

/** Start the Google sign-in flow via popup. */
export async function signInWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  await signInWithPopup(auth, provider);
}

/** Sign the current user out. */
export async function signOutUser(): Promise<void> {
  await signOut(auth);
}

/**
 * Watch the auth state. `onUser` fires with a validated internal user or `null`
 * (signed out / invalid). `onSignedOut` fires only on a transition from a
 * previously-signed-in state to `null` — used for the session-expired banner
 * (doc 147 §8.2 AUTH-03).
 */
export function watchAuth(
  onUser: (user: InternalUser | null) => void,
  onSignedOut: () => void,
): () => void {
  let wasSignedIn = false;
  return onAuthStateChanged(auth, async (fbUser) => {
    if (import.meta.env.DEV) console.warn('[atlas/auth] onAuthStateChanged →', fbUser ? `fbUser=${fbUser.email} verified=${fbUser.emailVerified}` : 'null');
    if (!fbUser) {
      if (wasSignedIn) onSignedOut();
      wasSignedIn = false;
      onUser(null);
      return;
    }
    try {
      const tokenResult = await fbUser.getIdTokenResult();
      if (import.meta.env.DEV) console.warn('[atlas/auth] claims →', JSON.stringify(tokenResult.claims));
      const internal = mapToInternalUser(fbUser, tokenResult.claims);
      if (import.meta.env.DEV) console.warn('[atlas/auth] mapToInternalUser →', internal ? `role=${internal.role}` : 'null (rejected)');
      wasSignedIn = internal !== null;
      onUser(internal);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[atlas/auth] getIdTokenResult ERROR →', err);
      wasSignedIn = false;
      onUser(null);
    }
  });
}

/* c8 ignore stop */
